import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Circle,
  HStack,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverFooter,
  PopoverHeader,
  PopoverTrigger,
  Skeleton,
  StackDivider,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";

import { BookingService } from "../services/booking.service";
import QRCode from "react-qr-code";

const bookingService = new BookingService();

const QrModal: React.FC<{
  booking_id: string;
  isOpen: boolean;
  onClose: (options?: { redirect?: string }) => void;
}> = ({ booking_id, isOpen, onClose }) => {
  const toast = useToast();

  const [keyCode, setKeyCode] = useState<string>();
  const [pin, setPin] = useState<string>();

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  const [bookingEndLoading, setBookingEndLoading] = useState<boolean>(false);

  useEffect(() => {
    if (isOpen) {
      getCredentials();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking_id, isOpen]);

  const getCredentials = async () => {
    setLoading(true);

    return bookingService
      .credentials(booking_id)
      .then((res) => {
        setKeyCode(res.data.data.keycode);
        setPin(res.data.data.pincode);
      })
      .catch((error) => {
        console.error(error);
        setError(true);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const endBooking = async (id: string) => {
    setBookingEndLoading(true);

    bookingService
      .end(id)
      .then(({ data }) => {
        console.log("[endBooking] data", data);

        onClose({
          redirect: "/bookings",
        });

        toast({
          title: "Reserva finalizada",
          description: "¡Tu reserva ha sido finalizada con éxito!",
          status: "success",
          duration: 5000,
          position: "top",
          isClosable: true,
        });
      })
      .catch((err) => {
        console.log("[endBooking] err", err);

        toast({
          title: "Error al finalizar reserva",
          description:
            "No se ha podido finalizar la reserva. Inténtalo de nuevo más tarde.",
          status: "error",
          duration: 5000,
          position: "top",
          isClosable: true,
        });
      })
      .finally(() => {
        setBookingEndLoading(false);
      });
  };

  return (
    <Modal isOpen={isOpen} onClose={() => onClose()} isCentered>
      <ModalOverlay backdropFilter="blur(2px)" />
      <ModalContent>
        <ModalCloseButton />

        <ModalBody as={VStack} spacing={8} px={4} pt={12} pb={4}>
          {error ? (
            <>
              <Alert
                status="error"
                variant="subtle"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                textAlign="center"
                height="200px"
              >
                <AlertIcon boxSize="40px" mr={0} />
                <AlertTitle mt={4} mb={1} fontSize="lg">
                  Error al obtener las credenciales del locker
                </AlertTitle>
                <AlertDescription maxWidth="sm">
                  Contacta con nosotros en info@salacity.es para obtener más
                  información.
                </AlertDescription>
              </Alert>
            </>
          ) : loading || !keyCode || !pin ? (
            <Skeleton w={"full"} aspectRatio={1} />
          ) : (
            <VStack
              spacing={8}
              divider={<StackDivider borderColor="gray.200" />}
            >
              {/* QR Code */}
              <VStack
                spacing={4}
                justifyContent={"center"}
                alignItems={"center"}
                textAlign={"center"}
                px={4}
              >
                <VStack spacing={1}>
                  <Heading as="h2" size="lg">
                    ¡Aquí tienes tu QR!
                  </Heading>

                  <Text fontSize="sm" color="gray.600">
                    Escanea este código en el lector para abrir tu locker
                  </Text>
                </VStack>
                <VStack spacing={12}>
                  <QRCode
                    size={256}
                    style={{ height: "auto", maxWidth: "250px", width: "100%" }}
                    value={keyCode}
                    viewBox={`0 0 256 256`}
                  />
                </VStack>
              </VStack>

              {/* PIN */}
              <VStack
                spacing={4}
                justifyContent={"center"}
                alignItems={"center"}
                textAlign={"center"}
                px={4}
              >
                <VStack spacing={1}>
                  <Heading as="h2" size="lg">
                    También puedes usar tu PIN
                  </Heading>

                  <Text fontSize="sm" color="gray.600">
                    Introduce el siguiente PIN en el teclado del lector
                  </Text>
                </VStack>

                <HStack
                  spacing={1}
                  divider={<Circle size={"6px"} bgColor={"gray.300"} />}
                >
                  {pin.split("").map((value, index) => (
                    <Box
                      key={index}
                      border={"2px solid"}
                      borderColor={"gray.300"}
                      borderRadius={"xl"}
                      py={3}
                      px={3}
                    >
                      <Text fontSize={"lg"} fontWeight={"semibold"}>
                        {value}
                      </Text>
                    </Box>
                  ))}
                </HStack>
              </VStack>
            </VStack>
          )}
        </ModalBody>

        <ModalFooter justifyContent={"center"}>
          <HStack spacing={4} justifyContent={"center"} alignItems={"center"}>
            <Popover>
              <PopoverTrigger>
                <Button colorScheme={"brand"} borderRadius={"full"}>
                  Finalizar reserva
                </Button>
              </PopoverTrigger>
              <PopoverContent>
                <PopoverArrow />
                <PopoverCloseButton />
                <PopoverHeader fontWeight={"semibold"}>
                  ¿Estás seguro?
                </PopoverHeader>
                <PopoverBody color={"gray.800"} fontSize={"sm"}>
                  Asegúrate de que has recogido todas tus pertenencias antes de
                  finalizar la reserva.
                </PopoverBody>
                <PopoverFooter as={HStack} justifyContent={"end"}>
                  <Button
                    size="sm"
                    colorScheme="brand"
                    isLoading={bookingEndLoading}
                    onClick={() => endBooking(booking_id)}
                  >
                    Confirmar
                  </Button>
                </PopoverFooter>
              </PopoverContent>
            </Popover>

            <Button borderRadius={"full"} onClick={() => onClose()}>
              Cerrar
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default QrModal;
